/**
 * The few lines at the top of a help page or release note: `key: value`
 * pairs between `---` fences. No YAML library; the keys are plain strings,
 * dates and a boolean.
 */

export interface Frontmatter {
  readonly [key: string]: string;
}

export interface ParsedMarkdown {
  readonly data: Frontmatter;
  readonly body: string;
}

/**
 * Splits a Markdown file into its frontmatter and body.
 *
 * @param source - The file's text.
 * @returns The key/value pairs (empty when there is no fence) and the rest.
 */
export function parseFrontmatter(source: string): ParsedMarkdown {
  const match = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/.exec(source);
  if (!match) return { data: {}, body: source };
  const data: Record<string, string> = {};
  for (const line of (match[1] ?? '').split(/\r?\n/)) {
    const pair = /^([A-Za-z][A-Za-z0-9_-]*)\s*:\s*(.*)$/.exec(line);
    if (!pair) continue;
    data[pair[1] ?? ''] = (pair[2] ?? '').trim().replace(/^["']|["']$/g, '');
  }
  return { data, body: source.slice(match[0].length) };
}

/** "true", "yes" and "1" are true; anything else is false. */
export function frontmatterFlag(value: string | undefined): boolean {
  return ['true', 'yes', '1'].includes((value ?? '').trim().toLowerCase());
}
